import { useEffect, useState } from 'react';
import { usePlanSelection } from '@/features/products';
import { ANALYTICS_EVENTS, trackEvent } from '@/features/analytics';
import { usePurchaseStore } from '@/stores/purchase-store';
import { useQuizStore } from '@/stores/quiz-store';
import { useNavigateWithParams } from '@/shared/hooks/use-navigate-with-params';
import { AppRoutes } from '@/shared/lib/routes';
import { useOffersFunnelStore } from '@/features/offers';
import { LoadingSpinner } from '@/shared/components/loading-spinner';
import { isValidEmail } from '@/shared/lib/email';
import { cn } from '@/lib/utils';
import { isApplePayAvailable, useCheckout } from '../hooks/use-checkout';
import { CheckoutHeader } from './checkout-header';
import { CheckoutHero } from './checkout-hero';
import { CheckoutAdvantages } from './checkout-advantages';
import { CheckoutPlans } from './checkout-plans';
import { CheckoutTrust } from './checkout-trust';
import { CheckoutClosing } from './checkout-closing';
import { CardPaymentSheet } from './card-payment-sheet';
import { ApplePaySheet } from './apple-pay-sheet';

type OpenSheet = 'card' | 'applepay' | null;

function scrollToPlans() {
  document.getElementById('checkout-plans')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function SubscriptionCheckout() {
  const navigate = useNavigateWithParams();
  const purchaseStatus = usePurchaseStore((s) => s.status);
  const email = (useQuizStore((s) => s.answers.email) as string) || '';
  const { visiblePlans, selectedPlan, setSelectedPlan, isLoading } = usePlanSelection();

  const [sheet, setSheet] = useState<OpenSheet>(null);
  const [applePayAvailable] = useState(isApplePayAvailable);
  const { payWithApplePay } = useCheckout(selectedPlan ?? undefined, email, { initCard: false });

  const goToOffers = () => {
    useOffersFunnelStore.getState().reset();
    navigate({ to: AppRoutes.ADDITIONAL_OFFERS });
  };

  const handleMarketingCta = () => {
    trackEvent(ANALYTICS_EVENTS.SETTLEMENT_SUB_SCREEN_BUTTON_CLICK);
    scrollToPlans();
  };

  useEffect(() => {
    if (!selectedPlan) return;
    trackEvent(ANALYTICS_EVENTS.PURCHASE_PAGE_VIEWED, {
      productId: selectedPlan.id,
      price: selectedPlan.price,
      currency: selectedPlan.currency,
    });
    trackEvent(ANALYTICS_EVENTS.SETTLEMENT_SUB_SCREEN_SHOWN);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlan?.id]);

  useEffect(() => {
    if (purchaseStatus === 'success') goToOffers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseStatus]);

  const handleSuccess = () => {
    setSheet(null);
    goToOffers();
  };

  const handleApplePay = async () => {
    trackEvent(ANALYTICS_EVENTS.SETTLEMENT_SUB_SCREEN_SUB_BUTTON_CLICK, {
      metadata: { method: 'apple' },
    });
    if (isValidEmail(email)) {
      const ok = await payWithApplePay();
      if (ok) handleSuccess();
    } else {
      setSheet('applepay');
    }
  };

  return (
    <div className="mx-auto min-h-[100dvh] w-full max-w-app bg-mf-neutral-50 font-body text-mf-neutral-900">
      {/* iOS 26 accessory bar snapshots this layer, not the sheet overlay.
          Blur the page itself so Rachel/Priya cannot show through the glass. */}
      <div className={cn(sheet && 'pointer-events-none blur-2xl')}>
        <CheckoutHeader onStartNow={handleMarketingCta} />

        {isLoading || !selectedPlan ? (
          <div className="flex min-h-[60dvh] items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <CheckoutHero onCta={handleMarketingCta} />
            <div className="flex flex-col gap-8 bg-white py-10">
              <CheckoutAdvantages />
              <CheckoutPlans
                plans={visiblePlans}
                selectedPlan={selectedPlan}
                onSelect={setSelectedPlan}
                applePayAvailable={applePayAvailable}
                onApplePay={handleApplePay}
                onCard={() => {
                  trackEvent(ANALYTICS_EVENTS.SETTLEMENT_SUB_SCREEN_SUB_BUTTON_CLICK, {
                    metadata: { method: 'card' },
                  });
                  setSheet('card');
                }}
              />
            </div>
            <CheckoutTrust />
            <CheckoutClosing onCta={handleMarketingCta} />
          </>
        )}
      </div>

      {selectedPlan && (
        <>
          <CardPaymentSheet
            plan={selectedPlan}
            email={email}
            open={sheet === 'card'}
            onClose={() => setSheet(null)}
            onSuccess={handleSuccess}
          />
          <ApplePaySheet
            plan={selectedPlan}
            email={email}
            open={sheet === 'applepay'}
            onClose={() => setSheet(null)}
            onSuccess={handleSuccess}
          />
        </>
      )}
    </div>
  );
}
